import { useState, useCallback, useMemo, useEffect, useLayoutEffect } from "react";
import { useParams, Link, Navigate, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import Nav from "@/components/landing/Nav";
import Footer from "@/components/landing/Footer";
import WhatsAppSticky from "@/components/landing/WhatsAppSticky";
import LeadDialog from "@/components/landing/LeadDialog";
import Reveal from "@/components/landing/Reveal";
import ProductCard from "@/components/products/ProductCard";
import { RECIPIENTS } from "@/data/products";
import { fetchProduct, fetchProducts } from "@/lib/api";

const labelFor = (list, value) => list.find((x) => x.value === value)?.label || value;
const resolveAsset = (u, w) => {
  if (!u) return "";
  const full = u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`;
  if (!w || !/\/api\/files\//.test(full)) return full;
  return `${full}${full.includes("?") ? "&" : "?"}w=${w}`;
};

/**
 * Reusable product detail page. Fetches product by :slug from the backend.
 * Adding a product via the Admin Dashboard automatically generates this page.
 */
export default function ProductPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setNotFound(false);
    fetchProduct(slug)
      .then((data) => {
        if (!alive) return;
        setProduct(data);
        setLoading(false);
        return fetchProducts({ collection: data.collection_slug || "diwali-collection" });
      })
      .then((list) => {
        if (!alive || !list) return;
        setRelated(list.filter((p) => p.slug !== slug).slice(0, 3));
      })
      .catch(() => { if (alive) { setNotFound(true); setLoading(false); } });
  }, [slug]);

  // Every product page should load from the top, regardless of prior scroll position.
  // useLayoutEffect ensures the reset fires before paint and after any browser
  // scroll restoration behaviour on navigation.
  useLayoutEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
    window.scrollTo(0, 0);
  }, [slug]);
  useEffect(() => {
    // Belt-and-braces: re-assert scroll after any late layout shifts (e.g. Reveal
    // components mounting or fonts settling).
    const t = setTimeout(() => window.scrollTo(0, 0), 60);
    return () => clearTimeout(t);
  }, [slug]);

  const [dialog, setDialog] = useState({ open: false, type: "bulk" });
  const openMeeting = useCallback(() => setDialog({ open: true, type: "meeting" }), []);
  const openBulk = useCallback(() => setDialog({ open: true, type: "bulk" }), []);
  const closeDialog = useCallback(() => setDialog((d) => ({ ...d, open: false })), []);
  const handlers = useMemo(() => ({ openMeeting, openBulk }), [openMeeting, openBulk]);

  const images = product && Array.isArray(product.images) ? product.images.filter(Boolean) : [];
  const hasImages = images.length > 0;

  const [activeIdx, setActiveIdx] = useState(0);
  useEffect(() => { setActiveIdx(0); }, [slug]);

  // Sync CMS meta_title / meta_description into <head> so product pages have
  // per-product SEO. Falls back to product name / short description when the
  // CMS fields are blank. Also emits OpenGraph tags (og:title / og:description
  // / og:image) using the same source fields + the primary product image.
  useEffect(() => {
    if (!product) return;
    const title = (product.meta_title && product.meta_title.trim())
      || `${product.name} | Utsah`;
    const description = (product.meta_description && product.meta_description.trim())
      || product.short_description
      || "";
    const primaryImage = Array.isArray(product.images) ? product.images.find(Boolean) : null;
    const imageUrl = primaryImage ? resolveAsset(primaryImage) : "";

    document.title = title;
    if (description) {
      const metas = document.querySelectorAll('meta[name="description"]');
      if (metas.length === 0) {
        const el = document.createElement("meta");
        el.setAttribute("name", "description");
        el.setAttribute("content", description);
        document.head.appendChild(el);
      } else {
        metas.forEach((el) => el.setAttribute("content", description));
      }
    }

    const setOg = (property, value) => {
      if (!value) return;
      let el = document.querySelector(`meta[property="${property}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("property", property);
        document.head.appendChild(el);
      }
      el.setAttribute("content", value);
    };
    setOg("og:type", "product");
    setOg("og:title", title);
    setOg("og:description", description);
    setOg("og:image", imageUrl);
    setOg("og:url", window.location.href);
  }, [product]);

  if (loading) {
    return (
      <div className="utsah-app">
        <Nav onBookMeeting={openMeeting} />
        <div className="section" style={{ minHeight: "50vh", display: "grid", placeItems: "center", textAlign: "center", color: "var(--u-dark)", opacity: 0.6, fontStyle: "italic" }}>
          Loading…
        </div>
        <Footer handlers={handlers} />
      </div>
    );
  }
  if (notFound || !product) {
    return <Navigate to="/diwali-collection" replace />;
  }

  const bulkPrice = `₹${product.price.toLocaleString("en-IN")}`;
  const mrpFormatted =
    product.mrp && Number(product.mrp) > 0
      ? `₹${Number(product.mrp).toLocaleString("en-IN")}`
      : null;
  const boxContentsCount = product.contentsCount ?? (product.contents ? product.contents.length : 0);
  // Product Page enquiries carry the product name + slug in the source so the admin
  // dashboard and email alert clearly show which hamper generated the lead.
  const enquirySource = `Product Page · ${product.name} (/diwali-collection/${product.slug})`;

  return (
    <div className="utsah-app">
      <Nav onBookMeeting={openMeeting} />

      {/* PRODUCT HERO */}
      <section className="section pd-hero" data-testid="pd-hero-section">
        <div className="section-inner pd-grid">
          <Reveal className="pd-media">
            {hasImages ? (
              <>
                <div className="pd-main-image" data-testid="pd-main-image">
                  <img
                    src={resolveAsset(images[activeIdx], 1600)}
                    alt={`${product.name} — view ${activeIdx + 1}`}
                    fetchpriority="high"
                    decoding="async"
                  />
                </div>
                {images.length > 1 && (
                  <div className="pd-thumbs" role="tablist" aria-label="Product image thumbnails" data-testid="pd-thumbs">
                    {images.map((src, i) => (
                      <button
                        type="button"
                        key={`${src}-${i}`}
                        role="tab"
                        aria-selected={i === activeIdx}
                        className={`pd-thumb ${i === activeIdx ? "active" : ""}`.trim()}
                        onClick={() => setActiveIdx(i)}
                        data-testid={`pd-thumb-${i}`}
                        aria-label={`Show image ${i + 1}`}
                      >
                        <img src={resolveAsset(src, 200)} alt="" loading="lazy" decoding="async" />
                      </button>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className="s3-image-placeholder pd-media-placeholder">
                <svg width="56" height="56" viewBox="0 0 64 64" fill="none" aria-hidden>
                  <rect x="8" y="16" width="48" height="36" rx="3" fill="var(--u-orange)" opacity="0.24" />
                  <rect x="14" y="10" width="36" height="8" rx="2" fill="var(--u-orange)" opacity="0.2" />
                </svg>
                <div className="add-image-label">{product.name}</div>
              </div>
            )}
            {product.badge && <span className="pd-badge" data-testid="pd-badge">{product.badge}</span>}
          </Reveal>

          <div className="pd-content">
            <Reveal className="pd-breadcrumb">
              <Link to="/diwali-collection">← Diwali Collection</Link>
            </Reveal>
            <Reveal as="h1" delay={1} className="section-headline pd-title">
              {product.name}
            </Reveal>
            <Reveal delay={2} className="pd-price" data-testid="pd-price">
              <span className="pd-price-label">Bulk Price</span>
              <span className="pd-price-value">{bulkPrice}</span>
              {mrpFormatted ? (
                <span className="pd-price-mrp" data-testid="pd-price-mrp">
                  MRP <s>{mrpFormatted}</s>
                </span>
              ) : null}
            </Reveal>
            <Reveal delay={2} className="pd-price-note" data-testid="pd-price-note">Tax &amp; Shipping Extra</Reveal>
            <Reveal as="p" delay={2} className="section-sub" style={{ marginTop: 20 }}>
              {product.short_description}
            </Reveal>

            <Reveal delay={3} className="pd-meta">
              <div className="pd-meta-row">
                <span className="pd-meta-label">Recipient</span>
                <span className="pd-meta-value">
                  {product.recipient.map((r) => labelFor(RECIPIENTS, r)).join(" · ")}
                </span>
              </div>
              <div className="pd-meta-row">
                <span className="pd-meta-label">Box Contents</span>
                <span className="pd-meta-value" data-testid="pd-box-contents">{(product.box_contents_label && product.box_contents_label.trim()) || `${boxContentsCount} ${boxContentsCount === 1 ? "Item" : "Items"}`}</span>
              </div>
              <div className="pd-meta-row">
                <span className="pd-meta-label">MOQ</span>
                <span className="pd-meta-value">50 units</span>
              </div>
            </Reveal>

            <Reveal delay={3} className="pd-cta-row">
              <button type="button" className="hero-cta" onClick={openBulk} data-testid="pd-enquire-btn">
                Enquire About This Hamper
                <svg width="18" height="12" viewBox="0 0 18 12" fill="none" aria-hidden><path d="M1 6h15M11 1l5 5-5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </button>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CONTENTS */}
      <section className="section pd-contents-section" data-testid="pd-contents-section">
        <div className="section-inner">
          <Reveal className="eyebrow">Inside the Hamper</Reveal>
          <Reveal as="h2" delay={1} className="section-headline">
            What's <em>Inside.</em>
          </Reveal>
          <div className="pd-contents-grid">
            {product.contents.map((item, i) => {
              const itemName = typeof item === "string" ? item : (item?.name || "");
              const itemDesc = typeof item === "object" ? (item?.description || "") : "";
              return (
                <Reveal key={`${itemName}-${i}`} delay={i > 3 ? 3 : i} className="pd-content-item">
                  <span className="pd-content-num">{String(i + 1).padStart(2, "0")}</span>
                  <span className="pd-content-text">
                    {itemName}
                    {itemDesc && <span className="pd-content-desc"> — {itemDesc}</span>}
                  </span>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* RELATED */}
      {related.length > 0 && (
        <section className="section pd-related" data-testid="pd-related-section">
          <div className="section-inner">
            <Reveal className="eyebrow" style={{ display: "inline-flex", marginLeft: "auto", marginRight: "auto" }}>You May Also Like</Reveal>
            <Reveal as="h2" delay={1} className="section-headline" style={{ textAlign: "center" }}>
              More <em>Curated Gifts.</em>
            </Reveal>
            <div className="dc-grid" style={{ marginTop: 48 }}>
              {related.map((p, i) => <ProductCard key={p.slug} product={p} index={i} />)}
            </div>
            <div style={{ textAlign: "center", marginTop: 40 }}>
              <button type="button" className="hero-cta" onClick={() => navigate("/diwali-collection")} data-testid="pd-back-to-collection">
                Explore Full Collection
                <svg width="18" height="12" viewBox="0 0 18 12" fill="none" aria-hidden><path d="M1 6h15M11 1l5 5-5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </button>
            </div>
          </div>
        </section>
      )}

      <Footer handlers={handlers} />
      <WhatsAppSticky />
      <LeadDialog open={dialog.open} type={dialog.type} source={enquirySource} onOpenChange={(v) => (v ? null : closeDialog())} />
      <Toaster position="bottom-center" richColors theme="light" toastOptions={{ style: { fontFamily: "Manrope, system-ui, sans-serif", fontWeight: 500 } }} />
    </div>
  );
}
